/**
 * UpgradeShowcaseOverlay — момент раскрытия после покупки апгрейда.
 *
 * JB 2026-05-25: «как то особо не вижу что появилось визуально не было
 * почему то шоукейса». Эта оверлей появляется на КАЖДУЮ покупку (а не
 * только первую) — игрок видит:
 *   - название апгрейда
 *   - новый уровень
 *   - что добавил этот уровень (например «+5% к тапу»)
 *   - подсказку visible-change («Свечение Искры стало глубже»)
 *
 * Авто-dismiss через 2.5 сек, тап по оверлею тоже закрывает (не блокирует
 * следующую покупку). Звук `visible_change_reveal` проигрывается из
 * `purchaseUpgrade` action — здесь только визуал. Через z-index сидим
 * выше UpgradesPanel но ниже MilestoneRevealOverlay (если milestone
 * artifact unlocked одновременно — он играет первым).
 */
import { motion, AnimatePresence } from 'framer-motion';
import { useEffect } from 'react';
import { UPGRADES_BY_ID } from '@sai-clicker/content';
import { describeUpgradeEffect } from '@sai-clicker/game-core';
import { useGameStore } from '../stores/game.js';
import styles from './UpgradeShowcaseOverlay.module.css';

const AUTO_DISMISS_MS = 2_500;

export function UpgradeShowcaseOverlay(): JSX.Element {
  const pending = useGameStore((s) => s.pendingUpgradeShowcase);
  const dismiss = useGameStore((s) => s.dismissUpgradeShowcase);

  useEffect(() => {
    if (pending === null) return;
    const timer = window.setTimeout(() => {
      dismiss();
    }, AUTO_DISMISS_MS);
    return () => {
      window.clearTimeout(timer);
    };
  }, [pending, dismiss]);

  return (
    <AnimatePresence>
      {pending !== null && (
        <ShowcaseCard
          key={pending.firedAt}
          upgradeId={pending.upgradeId}
          newLevel={pending.newLevel}
          onDismiss={dismiss}
        />
      )}
    </AnimatePresence>
  );
}

interface CardProps {
  readonly upgradeId: string;
  readonly newLevel: number;
  readonly onDismiss: () => void;
}

function ShowcaseCard({ upgradeId, newLevel, onDismiss }: CardProps): JSX.Element | null {
  const upgrade = UPGRADES_BY_ID.get(upgradeId);
  if (upgrade === undefined) return null;

  // Effect *after* this purchase = describeUpgradeEffect at newLevel.
  // We also compute the previous level's effect to highlight the delta.
  const after = describeUpgradeEffect(upgrade, newLevel);
  const before = describeUpgradeEffect(upgrade, newLevel - 1);

  // Pick the most useful line: prefer "+5% к тапу" delta if both available.
  const deltaLine = (() => {
    if (after.currentLabel !== null && before.currentLabel !== null) {
      // Strip "сейчас даёт " prefix from after and turn it into a delta line.
      return after.currentLabel.replace(/^сейчас даёт /, 'теперь даёт ');
    }
    return after.currentLabel ?? upgrade.visibleChange.description;
  })();

  return (
    <div className={styles.wrap}>
      {/* 2026-05-26 polish: spring physics для «материализации» апгрейда.
          Раньше плоский ease — выглядел office-like. Сейчас warm bounce
          (stiffness 300, damping 16) — card материализуется ритмично,
          как «приходит» в Рощу. Exit остаётся ease — растворяется мягко. */}
      <motion.button
        type="button"
        className={styles.card}
        onClick={onDismiss}
        aria-label={`${upgrade.name} — уровень ${newLevel.toString()}`}
        initial={{ opacity: 0, y: -16, scale: 0.92 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -8, scale: 0.97 }}
        transition={{
          opacity: { duration: 0.3, ease: 'easeOut' },
          y: { type: 'spring', stiffness: 300, damping: 16 },
          scale: { type: 'spring', stiffness: 300, damping: 16 },
        }}
      >
        <p className={styles.preface}>память пути</p>
        <h3 className={styles.title}>{upgrade.name}</h3>
        <p className={styles.level}>
          ур. {newLevel.toString()}
          {upgrade.maxLevel > 1 ? ` / ${upgrade.maxLevel.toString()}` : ''}
        </p>
        <p className={styles.delta}>{deltaLine}</p>
        <p className={styles.visible}>{upgrade.visibleChange.description}</p>
      </motion.button>
    </div>
  );
}
